import { type Database } from '@databases/model/database';
import { type Entry } from '@databases/model/entry';
import { type Field, FieldType } from '@databases/model/field';
import {
  type UserValue,
  type SelectValue,
  type Value,
  UserType,
  type TextValue,
  type NumberValue,
  type DateValue,
  type PageLinkValue,
  type HyperlinkValue,
  type JiraIssueValue
} from '@databases/model/value';
import { unparse } from 'papaparse';

import { type Warnings } from './warnings.ts';

export type JiraIssueDetailsResolver = (entry: Entry, field: Field) => string;

export type ConfluencePageConnectionResolver = (entry: Entry, field: Field) => string;

export type SelectedCells = Set<string>;

export type DatabaseEntryFieldValuesSnapshot = {
  fieldIds: readonly string[];
  valuesByEntryId: ReadonlyMap<string, ReadonlyMap<string, string>>;
};

export type CreateDatabaseEntryFieldValuesSnapshotOptions = {
  entryIds: readonly string[];
  fieldIds: readonly string[];
  resolveJiraIssueDetails?: JiraIssueDetailsResolver;
  resolveConfluencePageConnection?: ConfluencePageConnectionResolver;
};

export function getCellKey(entryId: string, fieldId: string): string {
  return `${entryId}:${fieldId}`;
}

export function createSelectedCells(cells: Array<[string, string]>): SelectedCells {
  return new Set(cells.map(([entryId, fieldId]) => getCellKey(entryId, fieldId)));
}

export function encodeDatabaseValue(
  value: Value<FieldType>,
  field: Field
): string | undefined {
  switch (value.type) {
    case FieldType.Text:
      return (value as TextValue).value.plain;
    case FieldType.Number:
      return (value as NumberValue).value.toString();
    case FieldType.Date:
      return (value as DateValue).value;
    case FieldType.Select:
      return (value as SelectValue).value
        .map(
          (value) =>
            field.selectOptions.options.find((option) => option.id === value)?.label
        )
        .filter(Boolean)
        .join('|');
    case FieldType.User:
      return (value as UserValue).value
        .map((user) =>
          user.type === UserType.Referenced ? user.accountId : 'Current User'
        )
        .join('|');
    case FieldType.PageLink:
      return (value as PageLinkValue).value
        .map((link) => link.ari || link.contentId || '')
        .filter(Boolean)
        .join('|');
    case FieldType.Hyperlink:
      return (value as HyperlinkValue).value.map((link) => link.url).join('|');
    case FieldType.JiraIssue:
      return (value as JiraIssueValue).value.map((issue) => issue.key).join('|');
    default:
      return undefined;
  }
}

function encodeDatabaseEntryFieldValue(
  entry: Entry,
  database: Database,
  fieldId: string,
  resolveJiraIssueDetails?: JiraIssueDetailsResolver,
  resolveConfluencePageConnection?: ConfluencePageConnectionResolver
): string {
  const field = database.fieldsById[fieldId];
  const value = entry.getValue(fieldId);

  if (value) {
    return encodeDatabaseValue(value, field) ?? '';
  }

  if (field.type === FieldType.JiraIssueDetails && resolveJiraIssueDetails) {
    return resolveJiraIssueDetails(entry, field);
  }

  if (
    (field.type === FieldType.PageLabels || field.type === FieldType.PageStatus) &&
    resolveConfluencePageConnection
  ) {
    return resolveConfluencePageConnection(entry, field);
  }

  return '';
}

/**
 * Captures encoded cell values once so validation and request serialization can reuse
 * the same values without resolving each field twice.
 */
export function createDatabaseEntryFieldValuesSnapshot(
  database: Database,
  {
    entryIds,
    fieldIds,
    resolveJiraIssueDetails,
    resolveConfluencePageConnection
  }: CreateDatabaseEntryFieldValuesSnapshotOptions
): DatabaseEntryFieldValuesSnapshot {
  const valuesByEntryId = new Map<string, ReadonlyMap<string, string>>();

  for (const entryId of entryIds) {
    const entry = database.entriesById[entryId];
    if (!entry) {
      continue;
    }

    const valuesByFieldId = new Map<string, string>();
    for (const fieldId of fieldIds) {
      valuesByFieldId.set(
        fieldId,
        encodeDatabaseEntryFieldValue(
          entry,
          database,
          fieldId,
          resolveJiraIssueDetails,
          resolveConfluencePageConnection
        )
      );
    }
    valuesByEntryId.set(entryId, valuesByFieldId);
  }

  return { fieldIds: [...fieldIds], valuesByEntryId };
}

function encodeDatabaseRow(
  entryId: string,
  entry: Entry,
  database: Database,
  index: number,
  visibleFieldIds: readonly string[],
  resolveJiraIssueDetails?: JiraIssueDetailsResolver,
  resolveConfluencePageConnection?: ConfluencePageConnectionResolver,
  entryFieldValuesSnapshot?: DatabaseEntryFieldValuesSnapshot
) {
  const row: Record<string, string> = {
    _id: String(index)
  };
  const snapshotValues = entryFieldValuesSnapshot?.valuesByEntryId.get(entryId);
  for (const fieldId of visibleFieldIds) {
    const field = database.fieldsById[fieldId];
    const snapshotValue = snapshotValues?.get(fieldId);
    row[field.name] =
      snapshotValue !== undefined
        ? snapshotValue
        : encodeDatabaseEntryFieldValue(
            entry,
            database,
            fieldId,
            resolveJiraIssueDetails,
            resolveConfluencePageConnection
          );
  }
  return row;
}

/**
 * Serializes the contents of a database.
 *
 * This will serialize as a CSV with the following format:
 *
 * _id,Field 1,Field 2,Field 3
 * 1,Value 1,Value 2,Value 3
 * 2,Value 4,Value 5,Value 6
 *
 * An `_id` field is added to the beginning of the row to identify the entry, which uses a 1-based index.
 * Multi-value fields (select, user, etc.) use pipe delimiters (e.g., Tag1 or Tag1|Tag2).
 *
 * @param database The database to serialize.
 * @param entryIds Optional pre-filtered/sorted entry IDs. If not provided, uses database.entries.
 * @param fieldIds Optional visible field IDs. If not provided, uses database.fields.
 * @returns The serialized contents.
 */
export function serializeDatabaseContents(
  database: Database,
  _warnings: Warnings,
  rowIdToEntryIdMappings: Map<string, string>,
  entryIds?: readonly string[],
  fieldIds?: readonly string[],
  resolveJiraIssueDetails?: JiraIssueDetailsResolver,
  resolveConfluencePageConnection?: ConfluencePageConnectionResolver,
  entryFieldValuesSnapshot?: DatabaseEntryFieldValuesSnapshot
): string {
  const entriesToSerialize = entryIds ?? database.entries;
  const fieldsToSerialize = fieldIds ?? database.fields;

  let rowIndex = 1;
  const rows = entriesToSerialize
    .map((entryId) => {
      const entry = database.entriesById[entryId];

      if (entry) {
        const row = encodeDatabaseRow(
          entryId,
          entry,
          database,
          rowIndex,
          fieldsToSerialize,
          resolveJiraIssueDetails,
          resolveConfluencePageConnection,
          entryFieldValuesSnapshot
        );
        rowIdToEntryIdMappings.set(String(rowIndex), entryId);
        rowIndex++;
        return row;
      }

      return undefined;
    })
    .filter(Boolean);

  const columns = fieldsToSerialize.map((fieldId) => database.fieldsById[fieldId].name);

  return unparse(rows, {
    header: true,
    escapeChar: '\\',
    columns: ['_id', ...columns]
  });
}

export type SerializedDatabaseContentsBatch = {
  output: string;
  rowIdToEntryIdMappings: Map<string, string>;
};

export type SerializeDatabaseContentsInBatchesOptions = {
  batchSize: number;
  entryIds?: readonly string[];
  fieldIds?: readonly string[];
  resolveJiraIssueDetails?: JiraIssueDetailsResolver;
  resolveConfluencePageConnection?: ConfluencePageConnectionResolver;
  entryFieldValuesSnapshot?: DatabaseEntryFieldValuesSnapshot;
};

export function serializeDatabaseContentsInBatches(
  database: Database,
  _warnings: Warnings,
  {
    batchSize,
    entryIds,
    fieldIds,
    resolveJiraIssueDetails,
    resolveConfluencePageConnection,
    entryFieldValuesSnapshot
  }: SerializeDatabaseContentsInBatchesOptions
): [SerializedDatabaseContentsBatch, ...SerializedDatabaseContentsBatch[]] {
  if (!Number.isInteger(batchSize) || batchSize <= 0) {
    throw new Error('Database content batch size must be a positive integer');
  }

  const entriesToSerialize = entryIds ?? database.entries;
  const fieldsToSerialize = fieldIds ?? database.fields;
  let encodedEntryCount = 0;
  const encodedEntries = entriesToSerialize
    .map((entryId) => {
      const entry = database.entriesById[entryId];

      if (entry) {
        const rowIndex = (encodedEntryCount % batchSize) + 1;
        encodedEntryCount++;
        return {
          entryId,
          row: encodeDatabaseRow(
            entryId,
            entry,
            database,
            rowIndex,
            fieldsToSerialize,
            resolveJiraIssueDetails,
            resolveConfluencePageConnection,
            entryFieldValuesSnapshot
          )
        };
      }

      return undefined;
    })
    .filter((entry): entry is NonNullable<typeof entry> => entry !== undefined);

  const columns = fieldsToSerialize.map((fieldId) => database.fieldsById[fieldId].name);
  const serializeBatch = (
    entries: typeof encodedEntries
  ): SerializedDatabaseContentsBatch => {
    const rowIdToEntryIdMappings = new Map<string, string>();
    const rows = entries.map(({ entryId, row }) => {
      rowIdToEntryIdMappings.set(row._id, entryId);
      return row;
    });

    return {
      output: unparse(rows, {
        header: true,
        escapeChar: '\\',
        columns: ['_id', ...columns]
      }),
      rowIdToEntryIdMappings
    };
  };

  if (encodedEntries.length === 0) {
    return [serializeBatch([])];
  }

  const [firstEntry, ...remainingEntries] = encodedEntries;
  const batches: [typeof encodedEntries, ...Array<typeof encodedEntries>] = [
    [firstEntry]
  ];
  for (const entry of remainingEntries) {
    const currentBatch = batches[batches.length - 1];
    if (currentBatch.length === batchSize) {
      batches.push([entry]);
    } else {
      currentBatch.push(entry);
    }
  }

  const [firstBatch, ...remainingBatches] = batches;
  return [serializeBatch(firstBatch), ...remainingBatches.map(serializeBatch)];
}

/**
 * Serializes the selected cells as a separate table.
 *
 * This will serialize as a CSV with the following format:
 *
 * selected_entry_id,selected_field
 * 1,Text
 * 2,Number
 *
 * @param selectedCells Set of selected cell keys in the format "entryId:fieldId"
 * @param database The database to get field names and entry IDs from
 * @param rowIdToEntryIdMappings Mapping from row IDs to entry IDs
 * @returns The serialized selected cells table.
 */
export function serializeSelectedCells(
  selectedCells: SelectedCells,
  database: Database,
  rowIdToEntryIdMappings: Map<string, string>
): string {
  const entryIdToRowId = new Map(
    Array.from(rowIdToEntryIdMappings.entries()).map(([rowId, entryId]) => [
      entryId,
      rowId
    ])
  );

  const rows = Array.from(selectedCells)
    .map((cellKey) => {
      const [entryId, fieldId] = cellKey.split(':');
      const rowId = entryIdToRowId.get(entryId);
      const field = database.fieldsById[fieldId];

      if (!rowId || !field) {
        return undefined;
      }

      return {
        selected_entry_id: rowId,
        selected_field: field.name
      };
    })
    .filter(Boolean);

  if (rows.length === 0) {
    return 'selected_entry_id,selected_field\r\n';
  }

  return unparse(rows, {
    header: true,
    escapeChar: '\\',
    columns: ['selected_entry_id', 'selected_field']
  });
}
